import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiFetch } from '../../api/client';

const initialState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  expiresAt: null,
  status: 'idle',
  error: null
};

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ username, password, deviceInfo }, { rejectWithValue }) => {
    try {
      const data = await apiFetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, deviceInfo }),
        skipAuth: true,
        autoLogout: false
      });
      const expiresIn = data.expiresIn ? Number(data.expiresIn) * 1000 : null;
      return {
        user: {
          username,
          name: username,
          email: username
        },
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresAt: expiresIn ? Date.now() + expiresIn : null
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to login right now.');
    }
  }
);

export const refreshAccessToken = createAsyncThunk(
  'auth/refreshAccessToken',
  async (_, { getState, rejectWithValue }) => {
    const { auth } = getState();
    if (!auth.refreshToken) {
      return rejectWithValue('No refresh token available.');
    }
    try {
      const data = await apiFetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refreshToken: auth.refreshToken,
          deviceInfo: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
        }),
        skipAuth: true
      });
      const expiresIn = data.expiresIn ? Number(data.expiresIn) * 1000 : null;
      return {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken || auth.refreshToken,
        expiresAt: expiresIn ? Date.now() + expiresIn : auth.expiresAt
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to refresh session.');
    }
  }
);

export const logoutUser = createAsyncThunk('auth/logoutUser', async (_, { getState }) => {
  const { auth } = getState();
  try {
    await apiFetch('/api/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refreshToken: auth.refreshToken
      })
    });
  } catch (error) {
    // ignore logout failures
  }
  return null;
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: () => ({
      ...initialState
    })
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.expiresAt = action.payload.expiresAt;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Invalid credentials.';
      })
      .addCase(refreshAccessToken.pending, (state) => {
        state.status = state.status === 'loading' ? 'loading' : 'refreshing';
        state.error = null;
      })
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.expiresAt = action.payload.expiresAt;
        state.error = null;
      })
      .addCase(refreshAccessToken.rejected, (state, action) => {
        state.status = 'failed';
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.expiresAt = null;
        state.error = action.payload || 'Unable to refresh session.';
      })
      .addCase(logoutUser.fulfilled, () => ({
        ...initialState
      }))
      .addCase(logoutUser.rejected, () => ({
        ...initialState
      }));
  }
});

export const { logout } = authSlice.actions;

export default authSlice.reducer;
